import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Collapse,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Close,
  AccessTime,
  LocalOffer,
  Info,
  Error as ErrorIcon,
  CheckCircle,
  SupportAgent,
} from '@mui/icons-material';

// ============================================================================
// TYPES - Extensible for future announcement types
// ============================================================================

export type AnnouncementType = 'warning' | 'info' | 'error' | 'success' | 'promo';

export interface Announcement {
  id: string;
  type: AnnouncementType;
  message: string;
  actionLabel?: string;
  actionUrl?: string;
  onAction?: () => void;
  dismissible?: boolean;
  icon?: React.ReactNode;
}

interface AnnouncementBannerProps {
  announcements?: Announcement[];
}

// ============================================================================
// STORAGE KEY for session persistence
// ============================================================================

const DISMISSED_KEY = 'edubook_dismissed_announcements';

const getDismissedAnnouncements = (): string[] => {
  try {
    const stored = sessionStorage.getItem(DISMISSED_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const setDismissedAnnouncement = (id: string): void => {
  try {
    const dismissed = getDismissedAnnouncements();
    if (!dismissed.includes(id)) {
      dismissed.push(id);
      sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(dismissed));
    }
  } catch {
    // Silently fail if sessionStorage is not available
  }
};

// ============================================================================
// THEME CONFIGURATION for different announcement types
// ============================================================================

const getAnnouncementStyles = (type: AnnouncementType, isDark: boolean) => {
  const configs = {
    warning: {
      light: {
        background: 'linear-gradient(135deg, #FFF8E1 0%, #FFECB3 100%)',
        borderColor: '#FFB300',
        iconBg: alpha('#FF8F00', 0.15),
        iconColor: '#F57C00',
        textColor: '#5D4037',
        actionColor: '#E65100',
      },
      dark: {
        background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.12) 0%, rgba(255, 167, 38, 0.08) 100%)',
        borderColor: alpha('#FFB300', 0.4),
        iconBg: alpha('#FF8F00', 0.2),
        iconColor: '#FFB74D',
        textColor: '#FFE0B2',
        actionColor: '#FFB74D',
      },
    },
    error: {
      light: {
        background: 'linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)',
        borderColor: '#EF5350',
        iconBg: alpha('#D32F2F', 0.15),
        iconColor: '#C62828',
        textColor: '#4E342E',
        actionColor: '#C62828',
      },
      dark: {
        background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.12) 0%, rgba(229, 115, 115, 0.08) 100%)',
        borderColor: alpha('#EF5350', 0.4),
        iconBg: alpha('#D32F2F', 0.2),
        iconColor: '#EF9A9A',
        textColor: '#FFCDD2',
        actionColor: '#EF9A9A',
      },
    },
    info: {
      light: {
        background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
        borderColor: '#42A5F5',
        iconBg: alpha('#1976D2', 0.15),
        iconColor: '#1565C0',
        textColor: '#1A237E',
        actionColor: '#1565C0',
      },
      dark: {
        background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.12) 0%, rgba(100, 181, 246, 0.08) 100%)',
        borderColor: alpha('#42A5F5', 0.4),
        iconBg: alpha('#1976D2', 0.2),
        iconColor: '#90CAF9',
        textColor: '#BBDEFB',
        actionColor: '#90CAF9',
      },
    },
    success: {
      light: {
        background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
        borderColor: '#66BB6A',
        iconBg: alpha('#388E3C', 0.15),
        iconColor: '#2E7D32',
        textColor: '#1B5E20',
        actionColor: '#2E7D32',
      },
      dark: {
        background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.12) 0%, rgba(129, 199, 132, 0.08) 100%)',
        borderColor: alpha('#66BB6A', 0.4),
        iconBg: alpha('#388E3C', 0.2),
        iconColor: '#A5D6A7',
        textColor: '#C8E6C9',
        actionColor: '#A5D6A7',
      },
    },
    promo: {
      light: {
        background: 'linear-gradient(135deg, #F3E5F5 0%, #E1BEE7 100%)',
        borderColor: '#AB47BC',
        iconBg: alpha('#7B1FA2', 0.15),
        iconColor: '#7B1FA2',
        textColor: '#4A148C',
        actionColor: '#7B1FA2',
      },
      dark: {
        background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.12) 0%, rgba(186, 104, 200, 0.08) 100%)',
        borderColor: alpha('#AB47BC', 0.4),
        iconBg: alpha('#7B1FA2', 0.2),
        iconColor: '#CE93D8',
        textColor: '#E1BEE7',
        actionColor: '#CE93D8',
      },
    },
  };

  return configs[type][isDark ? 'dark' : 'light'];
};

const getDefaultIcon = (type: AnnouncementType) => {
  const icons = {
    warning: <AccessTime sx={{ fontSize: 20 }} />,
    error: <ErrorIcon sx={{ fontSize: 20 }} />,
    info: <Info sx={{ fontSize: 20 }} />,
    success: <CheckCircle sx={{ fontSize: 20 }} />,
    promo: <LocalOffer sx={{ fontSize: 20 }} />,
  };
  return icons[type];
};

// ============================================================================
// SINGLE ANNOUNCEMENT ITEM COMPONENT
// ============================================================================

interface AnnouncementItemProps {
  announcement: Announcement;
  onDismiss: (id: string) => void;
}

const AnnouncementItem: React.FC<AnnouncementItemProps> = ({ announcement, onDismiss }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const styles = getAnnouncementStyles(announcement.type, isDark);

  const handleAction = () => {
    if (announcement.onAction) {
      announcement.onAction();
    } else if (announcement.actionUrl) {
      window.open(announcement.actionUrl, '_blank');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: { xs: 1.5, sm: 2 },
        px: { xs: 2, sm: 3 },
        py: 1.25,
        background: styles.background,
        borderBottom: `1px solid ${styles.borderColor}`,
        position: 'relative',
        overflow: 'hidden',
        // Subtle pattern overlay for elegance
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: isDark
            ? 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.03) 0%, transparent 50%)'
            : 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.5) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: styles.iconBg,
          color: styles.iconColor,
          flexShrink: 0,
        }}
      >
        {announcement.icon || getDefaultIcon(announcement.type)}
      </Box>

      {/* Message */}
      <Typography
        sx={{
          color: styles.textColor,
          fontSize: { xs: '0.8rem', sm: '0.875rem' },
          fontWeight: 500,
          letterSpacing: '0.01em',
          lineHeight: 1.4,
          textAlign: 'center',
          flex: 1,
          maxWidth: 600,
        }}
      >
        {announcement.message}
      </Typography>

      {/* Action Button */}
      {(announcement.actionLabel || announcement.onAction || announcement.actionUrl) && (
        <Button
          size="small"
          onClick={handleAction}
          startIcon={<SupportAgent sx={{ fontSize: 16 }} />}
          sx={{
            color: styles.actionColor,
            fontWeight: 600,
            fontSize: '0.75rem',
            textTransform: 'none',
            borderRadius: 2,
            px: 1.5,
            py: 0.5,
            minWidth: 'auto',
            whiteSpace: 'nowrap',
            bgcolor: isDark ? alpha(styles.actionColor, 0.1) : alpha(styles.actionColor, 0.08),
            border: `1px solid ${alpha(styles.actionColor, 0.3)}`,
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: isDark ? alpha(styles.actionColor, 0.2) : alpha(styles.actionColor, 0.15),
              borderColor: styles.actionColor,
              transform: 'translateY(-1px)',
            },
          }}
        >
          {announcement.actionLabel || 'Contact Support'}
        </Button>
      )}

      {/* Dismiss Button */}
      {announcement.dismissible !== false && (
        <IconButton
          size="small"
          onClick={() => onDismiss(announcement.id)}
          sx={{
            color: styles.textColor,
            opacity: 0.6,
            p: 0.5,
            transition: 'all 0.2s ease',
            '&:hover': {
              opacity: 1,
              bgcolor: alpha(styles.iconColor, 0.1),
            },
          }}
        >
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      )}
    </Box>
  );
};

// ============================================================================
// MAIN ANNOUNCEMENT BANNER COMPONENT
// ============================================================================

const AnnouncementBanner: React.FC<AnnouncementBannerProps> = ({ announcements = [] }) => {
  const [dismissedIds, setDismissedIds] = useState<string[]>([]);
  const [isVisible, setIsVisible] = useState(false);

  // Load dismissed announcements from sessionStorage on mount
  useEffect(() => {
    setDismissedIds(getDismissedAnnouncements());
    // Small delay for smooth entrance animation
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Filter out dismissed announcements
  const visibleAnnouncements = useMemo(() => {
    return announcements.filter((a) => !dismissedIds.includes(a.id));
  }, [announcements, dismissedIds]);

  const handleDismiss = (id: string) => {
    setDismissedAnnouncement(id);
    setDismissedIds((prev) => [...prev, id]);
  };

  // Don't render if no visible announcements
  if (visibleAnnouncements.length === 0) {
    return null;
  }

  return (
    <Collapse in={isVisible} timeout={400}>
      <Box
        sx={{
          position: 'relative',
          zIndex: (theme) => theme.zIndex.drawer + 2,
        }}
      >
        {visibleAnnouncements.map((announcement) => (
          <AnnouncementItem
            key={announcement.id}
            announcement={announcement}
            onDismiss={handleDismiss}
          />
        ))}
      </Box>
    </Collapse>
  );
};

export default AnnouncementBanner;
